import type {
  DeviceTileKind,
  DeviceTileVM,
  GlanceVM,
  HomeyDeviceSnap,
  HomeySnapshot,
  MediaVM,
  ZoneSectionVM,
} from "@/types/homey";

// ---------------------------------------------------------------------------
// Kind mapping
// ---------------------------------------------------------------------------

function kindFromClass(deviceClass: string): DeviceTileKind {
  switch (deviceClass) {
    case "light":
      return "light";
    case "socket":
      return "socket";
    case "airconditioning":
    case "thermostat":
    case "heater":
      return "climate";
    case "sensor":
      return "sensor";
    case "speaker":
    case "tv":
    case "settopbox":
      return "media";
    default:
      return "other";
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function numericCap(device: HomeyDeviceSnap, capId: string): number | null {
  const cap = device.capabilitiesObj[capId];
  if (cap === undefined) return null;
  return typeof cap.value === "number" ? cap.value : null;
}

function boolCap(device: HomeyDeviceSnap, capId: string): boolean | null {
  const cap = device.capabilitiesObj[capId];
  if (cap === undefined) return null;
  return typeof cap.value === "boolean" ? cap.value : null;
}

function stringCap(device: HomeyDeviceSnap, capId: string): string | null {
  const cap = device.capabilitiesObj[capId];
  if (cap === undefined) return null;
  return typeof cap.value === "string" && cap.value.length > 0 ? cap.value : null;
}

function hasCap(device: HomeyDeviceSnap, capId: string): boolean {
  return device.capabilities.includes(capId);
}

// ---------------------------------------------------------------------------
// toDeviceTileVM
// ---------------------------------------------------------------------------

export function toDeviceTileVM(device: HomeyDeviceSnap): DeviceTileVM {
  const kind = kindFromClass(device.deviceClass);

  // active
  let active = false;
  if (kind === "sensor") {
    // any alarm_* true
    active = device.capabilities.some((capId) => {
      if (!capId.startsWith("alarm_")) return false;
      return boolCap(device, capId) === true;
    });
  } else {
    active = boolCap(device, "onoff") === true;
  }

  // dim — only when dim is in capabilities list AND value is a number
  let dim: number | null = null;
  if (hasCap(device, "dim")) {
    const val = numericCap(device, "dim");
    if (typeof val === "number") dim = val;
  }

  // togglable
  const togglable = device.available && hasCap(device, "onoff");

  // temperatures — explicitly exclude .outdoorTemperature sub-capability
  const targetTemperature = numericCap(device, "target_temperature");
  const measuredTemperature = hasCap(device, "measure_temperature")
    ? numericCap(device, "measure_temperature")
    : null;

  // state — precedence chain
  let state: string;
  if (!device.available) {
    state = "Offline";
  } else if (kind === "light" && active && dim !== null) {
    state = `${Math.round(dim * 100)}%`;
  } else if (kind === "climate" && targetTemperature !== null) {
    state = `${targetTemperature}°`;
  } else if (hasCap(device, "onoff")) {
    state = active ? "On" : "Off";
  } else if (kind === "sensor" && measuredTemperature !== null) {
    state = `${measuredTemperature.toFixed(1)}°`;
  } else if (kind === "sensor" && active) {
    state = "Motion";
  } else {
    state = "...";
  }

  return {
    id: device.id,
    kind,
    name: device.name,
    state,
    active,
    available: device.available,
    dim,
    togglable,
    targetTemperature,
    measuredTemperature,
  };
}

// ---------------------------------------------------------------------------
// toMediaVM
// ---------------------------------------------------------------------------

/**
 * Build the media now-playing / control view-model for a `media`-kind device.
 *
 * Resolves transport capability ids per device: Volumio exposes speaker_prev /
 * speaker_next / speaker_playing; an Apple TV exposes remote_playpause and
 * (lacking discrete prev/next) maps prev/next onto the D-pad's remote_left /
 * remote_right. Controls are gated by a MediaCapabilities presence set so the
 * same dialog renders correctly for both device shapes.
 *
 * Artwork: only an absolute http(s) artwork_url is surfaced; anything else
 * (e.g. homeyimage:// resources) would require an authenticated image proxy,
 * which is out of scope here — the dialog falls back to a music-note placeholder.
 */
export function toMediaVM(device: HomeyDeviceSnap): MediaVM {
  // Prefer the actionable momentary remote_playpause when present (Apple TV);
  // speaker_playing on those devices is read-only state. Volumio has no
  // remote_playpause, so speaker_playing doubles as the toggle target there.
  const playPause = hasCap(device, "remote_playpause")
    ? "remote_playpause"
    : hasCap(device, "speaker_playing")
      ? "speaker_playing"
      : null;

  const prev = hasCap(device, "speaker_prev")
    ? "speaker_prev"
    : hasCap(device, "remote_left")
      ? "remote_left"
      : null;

  const next = hasCap(device, "speaker_next")
    ? "speaker_next"
    : hasCap(device, "remote_right")
      ? "remote_right"
      : null;

  const capabilities = {
    playPause: playPause !== null,
    prev: prev !== null,
    next: next !== null,
    volumeSet: hasCap(device, "volume_set"),
    volumeUpDown: hasCap(device, "volume_up") && hasCap(device, "volume_down"),
    mute: hasCap(device, "volume_mute"),
    shuffle: hasCap(device, "speaker_shuffle"),
    dpad:
      hasCap(device, "remote_up") &&
      hasCap(device, "remote_down") &&
      hasCap(device, "remote_left") &&
      hasCap(device, "remote_right") &&
      hasCap(device, "remote_select"),
    home: hasCap(device, "remote_home"),
    back: hasCap(device, "remote_back"),
  };

  const track = stringCap(device, "speaker_track");
  const artist = stringCap(device, "speaker_artist");
  const album = stringCap(device, "speaker_album");
  const playing = boolCap(device, "speaker_playing");

  const rawArtwork = stringCap(device, "artwork_url");
  const artworkUrl = rawArtwork && /^https?:\/\//i.test(rawArtwork) ? rawArtwork : null;

  // Compact summary: prefer "Artist — Track", then track alone, then play state.
  let summary: string;
  if (track !== null && artist !== null) {
    summary = `${artist} — ${track}`;
  } else if (track !== null) {
    summary = track;
  } else if (playing === true) {
    summary = "Playing";
  } else if (playing === false) {
    summary = "Paused";
  } else {
    summary = "Idle";
  }

  return {
    id: device.id,
    name: device.name,
    available: device.available,
    playing,
    track,
    artist,
    album,
    volume: capabilities.volumeSet ? numericCap(device, "volume_set") : null,
    hasVolumeSet: capabilities.volumeSet,
    muted: boolCap(device, "volume_mute"),
    shuffle: boolCap(device, "speaker_shuffle"),
    artworkUrl,
    summary,
    capabilities,
    caps: { prev, next, playPause },
  };
}

// ---------------------------------------------------------------------------
// buildZoneSections
// ---------------------------------------------------------------------------

/** Walk up the zone hierarchy to find the zone directly under the root (parent=null). */
function resolveFloorName(
  zoneId: string,
  zonesById: Map<string, { name: string; parent: string | null }>,
): string | null {
  const zone = zonesById.get(zoneId);
  if (!zone) return null;

  // Zone is the root itself
  if (zone.parent === null) return null;

  const parent = zonesById.get(zone.parent);
  if (!parent) return null;

  // Parent is the root (parent.parent === null), meaning zone is directly under root -> floor
  if (parent.parent === null) {
    // This zone IS the floor-level zone; zones directly under root get floorName null
    return null;
  }

  // Walk up further — return the floor name (zone directly under root)
  let current = zone;
  let floor: { name: string; parent: string | null } | undefined;

  while (current.parent !== null) {
    const p = zonesById.get(current.parent);
    if (!p) break;
    if (p.parent === null) {
      // current is the floor
      floor = current;
      break;
    }
    current = p;
  }

  return floor?.name ?? null;
}

const INTERACTIVE_KINDS = new Set<DeviceTileKind>(["light", "climate", "socket", "media"]);

function deviceSortKey(vm: DeviceTileVM): [number, string] {
  const group = INTERACTIVE_KINDS.has(vm.kind) ? 0 : vm.kind === "sensor" ? 1 : 2;
  return [group, vm.name];
}

export function buildZoneSections(
  snapshot: HomeySnapshot,
  hiddenIds: Set<string> = new Set(),
): ZoneSectionVM[] {
  const zonesById = new Map(snapshot.zones.map((z) => [z.id, { name: z.name, parent: z.parent }]));

  // Group devices by zone, excluding any the user has hidden.
  const devicesByZone = new Map<string, HomeyDeviceSnap[]>();
  for (const device of snapshot.devices) {
    if (hiddenIds.has(device.id)) continue;
    const list = devicesByZone.get(device.zoneId) ?? [];
    list.push(device);
    devicesByZone.set(device.zoneId, list);
  }

  const sections: ZoneSectionVM[] = [];

  for (const zone of snapshot.zones) {
    const zoneDevices = devicesByZone.get(zone.id);
    if (!zoneDevices || zoneDevices.length === 0) continue;

    const floorName = resolveFloorName(zone.id, zonesById);

    const vms = zoneDevices
      .map((d) => toDeviceTileVM(d))
      .sort((a, b) => {
        const [ag, an] = deviceSortKey(a);
        const [bg, bn] = deviceSortKey(b);
        if (ag !== bg) return ag - bg;
        return an.localeCompare(bn);
      });

    // Zone temperature: mean of measuredTemperature
    const temps = vms.map((v) => v.measuredTemperature).filter((t): t is number => t !== null);

    const temperature =
      temps.length > 0
        ? Math.round((temps.reduce((a, b) => a + b, 0) / temps.length) * 10) / 10
        : null;

    sections.push({
      id: zone.id,
      name: zone.name,
      floorName,
      devices: vms,
      temperature,
    });
  }

  // Sort: nulls first for floorName, then zone name localeCompare
  sections.sort((a, b) => {
    if (a.floorName === null && b.floorName !== null) return -1;
    if (a.floorName !== null && b.floorName === null) return 1;
    if (a.floorName !== b.floorName) {
      return (a.floorName ?? "").localeCompare(b.floorName ?? "");
    }
    return a.name.localeCompare(b.name);
  });

  return sections;
}

// ---------------------------------------------------------------------------
// buildGlance
// ---------------------------------------------------------------------------

export function buildGlance(snapshot: HomeySnapshot, hiddenIds: Set<string> = new Set()): GlanceVM {
  let powerSum = 0;
  let powerCount = 0;
  let lightsOn = 0;
  const insideTemps: number[] = [];
  const outsideTemps: number[] = [];
  let securityAlerts = 0;

  for (const device of snapshot.devices) {
    if (hiddenIds.has(device.id)) continue;
    const vm = toDeviceTileVM(device);

    // powerNowW
    const power = numericCap(device, "measure_power");
    if (power !== null) {
      powerSum += power;
      powerCount++;
    }

    // lightsOn
    if (vm.kind === "light" && vm.active) {
      lightsOn++;
    }

    // insideTemp — from measuredTemperature (already excludes outdoor sub-cap)
    if (vm.measuredTemperature !== null) {
      insideTemps.push(vm.measuredTemperature);
    }

    // outsideTemp — from measure_temperature.outdoorTemperature
    const outdoor = device.capabilitiesObj["measure_temperature.outdoorTemperature"];
    if (outdoor !== undefined && typeof outdoor.value === "number") {
      outsideTemps.push(outdoor.value);
    }

    // securityAlerts — alarm_* true, excluding alarm_battery
    for (const capId of device.capabilities) {
      if (!capId.startsWith("alarm_")) continue;
      if (capId === "alarm_battery") continue;
      if (boolCap(device, capId) === true) {
        securityAlerts++;
      }
    }
  }

  const powerNowW = powerCount > 0 ? powerSum : null;

  const insideTemp =
    insideTemps.length > 0
      ? Math.round((insideTemps.reduce((a, b) => a + b, 0) / insideTemps.length) * 10) / 10
      : null;

  const outsideTemp =
    outsideTemps.length > 0
      ? Math.round((outsideTemps.reduce((a, b) => a + b, 0) / outsideTemps.length) * 10) / 10
      : null;

  return {
    powerNowW,
    lightsOn,
    insideTemp,
    outsideTemp,
    securityAlerts,
  };
}
